/**
 * @file Contributor UI — publicationFieldConditionality.
 * Field enable/disable rules for the Contributor Publication form,
 * based on "Type of publication" selection.
 */

export const PUBLICATION_TYPE = {
  JOURNAL_ARTICLE: "JournalArticle",
  CONFERENCE_PAPER: "ConferencePaper",
  EPD: "EnvironmentalProductDeclaration",
  LCA_REPORT: "LifeCycleAssessmentReport",
  THESES: "Theses",
  INDUSTRIAL_REPORT: "IndustrialReport",
  GOVERNMENT_REPORT: "GovernmentReport",
  INDEPENDENT_ORG_REPORT: "IndependentOrganizationReport",
  ACADEMIC_ORG_REPORT: "AcademicOrganizationReport",
  MANUFACTURERS_ANNUAL_REPORT: "ManufacturersAnnualReport",
  WHITE_PAPER: "WhitePaper",
} as const;

const REPORT_TYPES = new Set<string>([
  PUBLICATION_TYPE.THESES,
  PUBLICATION_TYPE.INDUSTRIAL_REPORT,
  PUBLICATION_TYPE.GOVERNMENT_REPORT,
  PUBLICATION_TYPE.INDEPENDENT_ORG_REPORT,
  PUBLICATION_TYPE.ACADEMIC_ORG_REPORT,
  PUBLICATION_TYPE.MANUFACTURERS_ANNUAL_REPORT,
]);

function isHasDataPublishedFieldName(name: string): boolean {
  const n = name.toLowerCase();
  return n === "hasdatapublished" || n === "has_data_published";
}

function normalizeHasDataPublishedAnswer(value: string): "" | "yes" | "no" {
  const answer = String(value ?? "").trim().toLowerCase();
  if (answer === "yes" || answer === "no") return answer;
  return "";
}

const PUBLICATION_DATE_FIELDS = [
  "knowPublicationDate",
  "publicationYear",
  "publicationMonth",
  "publicationDay",
];

const EMAIL_TO_PUBLICATION_LINK = [
  "correspondingAuthorEmail",
  "publicationTitle",
  "publicationLink",
];

const EMAIL_TO_DOI = [
  "correspondingAuthorEmail",
  "publicationTitle",
  "publicationLink",
  "doi",
];

const EMAIL_TO_PRODUCT = [
  "correspondingAuthorEmail",
  "publicationTitle",
  "publicationLink",
  "doi",
  "journal",
  "volume",
  "conferenceName",
  "organizationName",
  ...PUBLICATION_DATE_FIELDS,
  "product",
];

const PRODUCT_TO_REFERENCE_SERVICE_LIFE = [
  "product",
  "relevantStandards",
  "epdRegistrationNumber",
  "epdPublicationYear",
  "epdPublicationMonth",
  "epdPublicationDay",
  "validityYear",
  "validityMonth",
  "validityDay",
  "geographicScopes",
  "country",
  "epdProgramme",
  "productCategoryRules",
  "complimentaryPcr",
  "lcaDatabasesUsed",
  "lcaToolUsed",
  "systemBoundaries",
  "cutOffCriteria",
  "allocationRules",
  "productionPlantKnown",
  "productionPlantLatitude",
  "productionPlantLongitude",
  "productionPlantState",
  "productionPlantDistrict",
  "productionPlantAddress",
  "productionPlantPincode",
  "functionalUnit",
  "referenceServiceLife",
];

const LCA_CORE_FIELDS = [
  "lcaDatabasesUsed",
  "lcaToolUsed",
  "systemBoundaries",
];

const PRODUCTION_PLANT_TO_FUNCTIONAL_UNIT = [
  "productionPlantKnown",
  "productionPlantLatitude",
  "productionPlantLongitude",
  "productionPlantState",
  "productionPlantDistrict",
  "productionPlantAddress",
  "productionPlantPincode",
  "functionalUnit",
];

const LCA_REPORT_LOCKED = [
  "doi",
  "journal",
  "volume",
  "conferenceName",
  "organizationName",
  "epdRegistrationNumber",
  "epdPublicationYear",
  "epdPublicationMonth",
  "epdPublicationDay",
  "validityYear",
  "validityMonth",
  "validityDay",
  "geographicScopes",
  "epdProgramme",
  "productCategoryRules",
  "complimentaryPcr",
];

/** Exported function — is Functional Unit Unit Field Name. */
export function isFunctionalUnitUnitFieldName(name: string): boolean {
  const n = name.toLowerCase();
  return (
    n === "functionalunitunit" ||
    n === "functionalunitunitid" ||
    n === "functional_unit_unit" ||
    n === "functional_unit_unit_id" ||
    (n.includes("functionalunit") && n.includes("unit") && n !== "functionalunit")
  );
}

/** Exported function — is Has Data Published Field. */
export function isHasDataPublishedField(field: {
  name?: string;
  label?: string;
}): boolean {
  const name = String(field.name || "").toLowerCase();
  const label = String(field.label || "").toLowerCase();
  return (
    name === "hasdatapublished" ||
    name === "has_data_published" ||
    label.includes("has the data been published")
  );
}

function unionFields(...groups: string[][]): Set<string> {
  const out = new Set<string>();
  groups.forEach((group) => {
    group.forEach((v) => out.add(v));
  });
  return out;
}

function subtractFields(base: string[], remove: string[]): Set<string> {
  const removeSet = new Set(remove);
  const out = new Set<string>();
  base.forEach((v) => {
    if (!removeSet.has(v)) out.add(v);
  });
  return out;
}

function getActiveFieldsForType(typeOfPublication: string): Set<string> | null {
  switch (typeOfPublication) {
    case PUBLICATION_TYPE.JOURNAL_ARTICLE:
      return subtractFields(EMAIL_TO_PRODUCT, ["conferenceName", "organizationName"]);

    case PUBLICATION_TYPE.CONFERENCE_PAPER:
      return subtractFields(EMAIL_TO_PRODUCT, ["journal", "volume"]);

    case PUBLICATION_TYPE.EPD:
      return unionFields(["correspondingAuthorEmail"], PRODUCT_TO_REFERENCE_SERVICE_LIFE);

    case PUBLICATION_TYPE.LCA_REPORT:
      return subtractFields(
        [...EMAIL_TO_PRODUCT, ...PRODUCT_TO_REFERENCE_SERVICE_LIFE],
        LCA_REPORT_LOCKED
      );

    case PUBLICATION_TYPE.WHITE_PAPER:
      return unionFields(
        EMAIL_TO_DOI,
        PUBLICATION_DATE_FIELDS,
        ["product"],
        LCA_CORE_FIELDS,
        PRODUCTION_PLANT_TO_FUNCTIONAL_UNIT
      );

    default:
      if (REPORT_TYPES.has(typeOfPublication)) {
        return unionFields(
          EMAIL_TO_PUBLICATION_LINK,
          PUBLICATION_DATE_FIELDS,
          ["product"],
          LCA_CORE_FIELDS,
          PRODUCTION_PLANT_TO_FUNCTIONAL_UNIT
        );
      }
      return null;
  }
}

function isFieldInActiveSet(fieldName: string, activeFields: Set<string>): boolean {
  if (activeFields.has(fieldName)) return true;
  if (isFunctionalUnitUnitFieldName(fieldName) && activeFields.has("functionalUnit")) {
    return true;
  }
  return false;
}

/**
 * Returns true when the field should be rendered disabled (locked).
 */
export function isPublicationFieldDisabled(params: {
  fieldName: string;
  typeOfPublication: string;
  hasDataPublished: string;
}): boolean {
  const { fieldName, typeOfPublication, hasDataPublished } = params;

  if (isHasDataPublishedFieldName(fieldName)) return false;

  const publishedAnswer = normalizeHasDataPublishedAnswer(hasDataPublished);

  // Until Yes/No is selected, only "Has the data been published?" stays enabled.
  if (!publishedAnswer) return true;

  // When "No" is selected, lock everything except that question.
  if (publishedAnswer === "no") return true;

  if (fieldName === "typeOfPublication") return false;
  if (fieldName === "uploadPublicationFile") return false;

  const type = String(typeOfPublication ?? "").trim();
  if (!type) return true;

  const activeFields = getActiveFieldsForType(type);
  if (!activeFields) return true;

  return !isFieldInActiveSet(fieldName, activeFields);
}
